import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSelector, { useLanguage } from '../../components/feature/LanguageSelector';
import { translations } from '../../mocks/translations';
import { useSEO, generateWebPageSchema, generateFAQSchema } from '../../utils/seo';

export default function ToursPage() {
  const [expandedTour, setExpandedTour] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const language = useLanguage();
  const t = translations[language];
  const location = useLocation();

  // SEO Configuration
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const faqData = [
    { question: t.tours.faq1Question, answer: t.tours.faq1Answer },
    { question: t.tours.faq2Question, answer: t.tours.faq2Answer },
    { question: t.tours.faq3Question, answer: t.tours.faq3Answer },
    { question: t.tours.faq4Question, answer: t.tours.faq4Answer },
    { question: t.tours.faq5Question, answer: t.tours.faq5Answer },
    { question: t.tours.faq6Question, answer: t.tours.faq6Answer },
    { question: t.tours.faq7Question, answer: t.tours.faq7Answer },
    { question: t.tours.faq8Question, answer: t.tours.faq8Answer }
  ];

  useSEO({
    title: 'Tours en Puerto Plata - Cascadas Damajagua, City Tour, Monos | Leury Tours',
    description: 'Descubre los mejores tours en Puerto Plata: Cascadas de Damajagua, City Tour, Tour de Monos, ATV y Buggies. Guías certificados, grupos pequeños, mejor precio garantizado.',
    keywords: 'tours Puerto Plata, Cascadas Damajagua, tour ciudad Puerto Plata, tour monos, ATV Puerto Plata, excursiones República Dominicana',
    ogImage: 'https://public.readdy.ai/ai/img_res/d5062ace114eb0648dc4bafcce3a479d.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema({
          name: 'Tours en Puerto Plata - Leury Tours',
          description: 'Explora los mejores tours y excursiones en Puerto Plata con guías certificados',
          url: `${siteUrl}/tours`
        }),
        generateFAQSchema(faqData)
      ]
    }
  });

  // Payment form state
  const [selectedTour, setSelectedTour] = useState<number | null>(null);
  const [vehicleOption, setVehicleOption] = useState<'atv' | 'buggy' | null>(null);
  const [adults, setAdults] = useState(2);
  const [kids, setKids] = useState(0);
  const [youngGuys, setYoungGuys] = useState(0);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Scroll to top when page loads
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const tours = [
    {
      id: 1,
      name:
        language === 'en'
          ? 'Waterfalls of Damajagua Tour – Natural Adventure in Puerto Plata'
          : 'Tour a las Cascadas de Damajagua – Aventura Natural en Puerto Plata',
      category: language === 'en' ? 'adventure' : 'aventura',
      duration: language === 'en' ? '4 hours' : '4 horas',
      groupSize: language === 'en' ? '2-11 people' : '2-11 personas',
      price: 55,
      rating: 4.9,
      reviews: 342,
      image:
        'https://readdy.ai/api/search-image?query=Damajagua%2027%20waterfalls%20adventure%20tour%20in%20Dominican%20Republic%20with%20crystal%20clear%20turquoise%20water%20cascading%20down%20natural%20rock%20formations%2C%20tourists%20jumping%20into%20natural%20pools%2C%20tropical%20rainforest%20setting%2C%20exciting%20adventure%20activity%2C%20professional%20travel%20photography%2C%20vibrant%20nature%20colors%2C%20simple%20natural%20background&width=800&height=600&seq=tour1&orientation=landscape',
      shortDescription:
        language === 'en'
          ? 'Jump, slide, and swim through waterfalls in the Dominican Republic.'
          : 'Salta, deslízate y nada a través de cascadas en la República Dominicana.',
      fullDescription:
        language === 'en'
          ? "Enjoy an unforgettable experience at one of Puerto Plata's most iconic natural attractions: the Waterfalls of Damajagua. This tour is perfect for travelers looking for adventure, nature, and fun in a safe and accessible experience. Accompanied by certified local guides, you'll hike through lush tropical surroundings before reaching a series of stunning natural pools. Along the way, you'll have the chance to walk, swim, slide, and jump into crystal-clear waters, all with the required safety equipment included. The tour lasts approximately 4 hours including round-trip transportation from the cruise port or your hotel in Puerto Plata. ✨ Ideal for couples, small and big groups, ✨ No previous experience required, ✨ Adventure, nature, and fun in one tour, ✨ Close shoes or water shoes are required, ✨ Bring a bottle of water with you during the excursion (we will provide it for you)."
          : "Disfruta de una experiencia inolvidable en una de las atracciones naturales más icónicas de Puerto Plata: las Cascadas de Damajagua. Este tour es perfecto para viajeros que buscan aventura, naturaleza y diversión en una experiencia segura y accesible. Acompañado por guías locales certificados, caminarás a través de exuberantes alrededores tropicales antes de llegar a una serie de impresionantes piscinas naturales. En el camino, tendrás la oportunidad de caminar, nadar, deslizarte y saltar a aguas cristalinas, todo con el equipo de seguridad requerido incluido. El tour dura aproximadamente 4 horas e incluye transporte de ida y vuelta desde el puerto de cruceros o tu hotel en Puerto Plata. ✨ Perfecto para parejas, grupos pequeños y grandes, ✨ No se requiere experiencia previa, ✨ Aventura, naturaleza y diversión en un solo tour, ✨ Se requieren zapatos cerrados o zapatos de agua, ✨ Trae una botella de agua contigo durante la excursión (te la proporcionaremos).",
      inclusions:
        language === 'en'
          ? [
              'Round-trip Transportation',
              'Certified Bilingual Guide',
              'Safety Equipment & Life Jacket',
              'Entrance Fees',
              'Waterproof Bag',
            ]
          : [
              'Transporte de Ida y Vuelta',
              'Guía Bilingüe Certificado',
              'Equipo de Seguridad y Chaleco Salvavidas',
              'Tarifas de Entrada',
              'Bolsa Impermeable',
            ],
      highlights:
        language === 'en'
          ? [
              'Jump into natural turquoise pools',
              'Slide down natural rock water slides',
              'Hike through tropical rainforest',
              'Enjoy traditional Dominican cuisine',
              'Professional safety equipment provided',
            ]
          : [
              'Salta a piscinas naturales turquesas',
              'Deslízate por toboganes de roca natural',
              'Camina por la selva tropical',
              'Disfruta de la cocina dominicana tradicional',
              'Equipo de seguridad profesional incluido',
            ],
    },
    {
      id: 2,
      name:
        language === 'en'
          ? 'Puerto Plata City Tour - Culture, History & Local Flavor'
          : 'Tour de la Ciudad de Puerto Plata - Cultura, Historia y Sabor Local',
      category: language === 'en' ? 'cultural' : 'cultural',
      duration: language === 'en' ? '2-5 hours' : '2-5 horas',
      groupSize: language === 'en' ? '2-11 people' : '2-11 personas',
      price: 65,
      rating: 4.8,
      reviews: 289,
      image:
        'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/0c218d36501bcfc1139625f2b6200986.jpeg',
      shortDescription:
        language === 'en'
          ? 'Explore the rich culture, fascinating history, and authentic local flavors of Puerto Plata.'
          : 'Explora la rica cultura, la fascinante historia y los auténticos sabores locales de Puerto Plata.',
      fullDescription:
        language === 'en'
          ? "Discover the magic of Puerto Plata on this guided City Tour, perfect for visitors who want to experience the highlights of the city in a short and enjoyable time. This tour takes you through the most iconic spots of Puerto Plata, including the colorful Umbrella Street, Pink street (Paseo de Doña Blanca), the historic Fort San Felipe, Central Park and the Cathedral of San Felipe and the Malecón where you'll enjoy beautiful ocean views and photo opportunities. You'll also visit local shops to learn about Dominican rum, cigars, chocolate, and amber, some of the country's most famous products. With a relaxed pace and the guidance of knowledgeable local guides, this city tour offers a great mix of culture, history, and everyday Dominican life. The experience lasts approximately 3 hours and includes round-trip transportation from the cruise port or your hotel in Puerto Plata. ✨ Perfect for cruise passengers ✨ Comfortable, informative, and family-friendly ✨ A great introduction to Puerto Plata"
          : "Descubre la magia de Puerto Plata en este Tour de Ciudad guiado, perfecto para visitantes que quieren experimentar los puntos destacados de la ciudad en un tiempo corto y agradable. Este tour te lleva a través de los lugares más icónicos de Puerto Plata, incluyendo la colorida Calle de los Paraguas, la Calle Rosa (Paseo de Doña Blanca), el histórico Fuerte San Felipe, el Parque Central y la Catedral de San Felipe y el Malecón donde disfrutarás de hermosas vistas al mar y oportunidades para fotos. También visitarás tiendas locales para aprender sobre el ron dominicano, cigarros, chocolate y ámbar, algunos de los productos más famosos del país. Con un ritmo relajado y la guía de conocedores locales, este tour de ciudad ofrece una gran mezcla de cultura, historia y vida cotidiana dominicana. La experiencia dura aproximadamente 3 horas e incluye transporte de ida y vuelta desde el puerto de cruceros o tu hotel en Puerto Plata. ✨ Perfecto para pasajeros de cruceros ✨ Cómodo, informativo y familiar ✨ Una gran introducción a Puerto Plata",
      inclusions:
        language === 'en'
          ? [
              'Round-trip Transportation',
              'Certified Bilingual Guide',
              'Entrance Fees',
            ]
          : [
              'Transporte de Ida y Vuelta',
              'Guía Bilingüe Certificado',
              'Tarifas de Entrada',
            ],
      highlights:
        language === 'en'
          ? [
              'Visit the historic Fort San Felipe (1577)',
              'Explore Victorian gingerbread architecture',
              'Visit the famous Amber Museum',
              'Taste authentic Dominican street food & local flavors',
            ]
          : [
              'Visita el histórico Fuerte San Felipe (1577)',
              'Explora la arquitectura victoriana',
              'Visita el famoso Museo del Ámbar',
              'Prueba auténtica comida callejera dominicana y sabores locales',
            ],
    },
    {
      id: 3,
      name:
        language === 'en'
          ? 'Monkey Tour – Fun & Interactive Experience in Puerto Plata'
          : 'Tour de Monos – Experiencia Divertida e Interactiva en Puerto Plata',
      category: language === 'en' ? 'nature' : 'naturaleza',
      duration: language === 'en' ? '2-3 hours' : '2-3 horas',
      groupSize: language === 'en' ? '2-11 people' : '2-11 personas',
      price: 75,
      kidsPrice: 50,
      youngGuysPrice: 55,
      rating: 4.9,
      reviews: 198,
      image:
        'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/69b1cb2120e4d8ed9550826bece4053e.jpeg',
      shortDescription:
        language === 'en'
          ? 'Get up close with adorable monkeys in their tropical habitat — an unforgettable wildlife encounter.'
          : 'Acércate a adorables monos en su hábitat tropical — un encuentro inolvidable con la vida silvestre.',
      fullDescription:
        language === 'en'
          ? "Enjoy a unique and unforgettable experience at Monkey Home, one of Puerto Plata's most fun and family-friendly attractions. This interactive tour gives you the chance to get up close with friendly squirrel monkeys in a beautiful natural setting. During the visit, knowledgeable guides will introduce you to the monkeys and share interesting facts about their behavior and habitat. You'll have the opportunity to interact, take photos, and even feed the monkeys as they climb onto your shoulders, creating memorable moments for visitors of all ages. The tour lasts approximately 3 to 4 hours and includes round-trip transportation from the cruise port or your hotel in Puerto Plata, ensuring a smooth and comfortable experience from start to finish. ✨ Perfect for families, couples, and cruise passengers ✨ Safe, fun, and educational ✨ A one-of-a-kind experience you'll never forget"
          : "Disfruta de una experiencia única e inolvidable en Monkey Home, una de las atracciones más divertidas y familiares de Puerto Plata. Este tour interactivo te da la oportunidad de acercarte a simpáticos monos ardilla en un hermoso entorno natural. Durante la visita, guías conocedores te presentarán a los monos y compartirán datos interesantes sobre su comportamiento y hábitat. Tendrás la oportunidad de interactuar, tomar fotos e incluso alimentar a los monos mientras se suben a tus hombros, creando momentos memorables para visitantes de todas las edades. El tour dura aproximadamente 3 a 4 horas e incluye transporte de ida y vuelta desde el puerto de cruceros o tu hotel en Puerto Plata, asegurando una experiencia cómoda de principio a fin. ✨ Perfecto para familias, parejas y pasajeros de cruceros ✨ Seguro, divertido y educativo ✨ Una experiencia única que nunca olvidarás",
      inclusions:
        language === 'en'
          ? [
              'Round-trip Transportation',
              'Bilingual Guide',
              'Sanctuary Entrance Fee',
              'Monkey Interaction Experience',
              'Nature Walk',
              'Photo Opportunities',
            ]
          : [
              'Transporte de Ida y Vuelta',
              'Guía Bilingüe',
              'Tarifa de Entrada al Santuario',
              'Experiencia de Interacción con Monos',
              'Caminata por la Naturaleza',
              'Oportunidades para Fotos',
            ],
      highlights:
        language === 'en'
          ? [
              'Interact with free-roaming squirrel monkeys',
              'Monkeys climb on your shoulders for photos',
              'Guided nature walk through tropical forest',
              'Learn about wildlife conservation efforts',
              'Perfect for families and animal lovers',
            ]
          : [
              'Interactúa con monos ardilla en libertad',
              'Los monos se suben a tus hombros para fotos',
              'Caminata guiada por el bosque tropical',
              'Aprende sobre esfuerzos de conservación',
              'Perfecto para familias y amantes de los animales',
            ],
    },
    {
      id: 4,
      name:
        language === 'en'
          ? 'Combined Tour: Monkey & Waterfalls of Damajagua – Ultimate Adventure'
          : 'Tour Combinado: Monos y Cascadas de Damajagua – Aventura Definitiva',
      category: language === 'en' ? 'combined' : 'combinado',
      duration: language === 'en' ? '4:30 hours' : '4:30 horas',
      groupSize: language === 'en' ? '2-11 people' : '2-11 personas',
      price: 90,
      kidsPrice: 75,
      youngGuysPrice: 80,
      rating: 5.0,
      reviews: 156,
      image:
        'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/008f4a42c81d14566b1fe447242294f5.png',
      shortDescription:
        language === 'en'
          ? 'The best of both worlds! Meet adorable monkeys and conquer the famous Damajagua waterfalls in one epic adventure.'
          : '¡Lo mejor de ambos mundos! Conoce adorables monos y conquista las famosas cascadas de Damajagua en una aventura épica.',
      fullDescription:
        language === 'en'
          ? "Experience the ultimate Puerto Plata adventure with our most popular combined tour! Start your day with an unforgettable encounter at Monkey Home, where friendly squirrel monkeys will climb onto your shoulders for amazing photo opportunities. After bonding with these adorable creatures, head to the world-famous 27 Waterfalls of Damajagua for an adrenaline-pumping experience. Jump, slide, and swim through crystal-clear natural pools surrounded by lush tropical rainforest. This 4.5-hour adventure combines wildlife interaction with thrilling water activities, giving you two incredible experiences in one day. ✨ Perfect for adventure seekers who want it all ✨ Save time and money with this combo deal ✨ Includes all safety equipment and bilingual guides ✨ Round-trip transportation from cruise port or hotel"
          : "¡Experimenta la aventura definitiva de Puerto Plata con nuestro tour combinado más popular! Comienza tu día con un encuentro inolvidable en Monkey Home, donde simpáticos monos ardilla se subirán a tus hombros para increíbles oportunidades de fotos. Después de conectar con estas adorables criaturas, dirígete a las mundialmente famosas 27 Cascadas de Damajagua para una experiencia llena de adrenalina. Salta, deslízate y nada a través de piscinas naturales de agua cristalina rodeadas de exuberante selva tropical. Esta aventura de 4.5 horas combina interacción con vida silvestre con emocionantes actividades acuáticas, dándote dos experiencias increíbles en un día. ✨ Perfecto para buscadores de aventuras que lo quieren todo ✨ Ahorra tiempo y dinero con este combo ✨ Incluye todo el equipo de seguridad y guías bilingües ✨ Transporte de ida y vuelta desde puerto de cruceros u hotel",
      inclusions:
        language === 'en'
          ? [
              'Round-trip Transportation',
              'Certified Bilingual Guides',
              'Monkey Sanctuary Entrance',
              'Waterfall Entrance Fees',
              'Safety Equipment & Life Jacket',
              'Waterproof Bag',
              'Photo Opportunities',
            ]
          : [
              'Transporte de Ida y Vuelta',
              'Guías Bilingües Certificados',
              'Entrada al Santuario de Monos',
              'Tarifas de Entrada a Cascadas',
              'Equipo de Seguridad y Chaleco Salvavidas',
              'Bolsa Impermeable',
              'Oportunidades para Fotos',
            ],
      highlights:
        language === 'en'
          ? [
              'Two amazing experiences in one tour',
              'Interact with playful squirrel monkeys',
              'Jump and slide through natural waterfalls',
              'Swim in crystal-clear turquoise pools',
              'Best value combo adventure in Puerto Plata',
            ]
          : [
              'Dos experiencias increíbles en un tour',
              'Interactúa con juguetones monos ardilla',
              'Salta y deslízate por cascadas naturales',
              'Nada en piscinas turquesas cristalinas',
              'Mejor combo de aventura en Puerto Plata',
            ],
    },
    {
      id: 5,
      name:
        language === 'en'
          ? 'Combined Tour: Monkey & City Tour – Culture Meets Nature'
          : 'Tour Combinado: Monos y Ciudad – Cultura y Naturaleza',
      category: language === 'en' ? 'combined' : 'combinado',
      duration: language === 'en' ? '4 hours' : '4 horas',
      groupSize: language === 'en' ? '2-11 people' : '2-11 personas',
      price: 90,
      kidsPrice: 75,
      youngGuysPrice: 80,
      rating: 4.9,
      reviews: 124,
      image:
        'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/96ecaa81034f7f593aea6d14cd264ba2.png',
      shortDescription:
        language === 'en'
          ? 'Discover Puerto Plata\'s rich culture and meet adorable monkeys – the perfect blend of history and wildlife!'
          : '¡Descubre la rica cultura de Puerto Plata y conoce adorables monos – la mezcla perfecta de historia y vida silvestre!',
      fullDescription:
        language === 'en'
          ? "Combine the best of Puerto Plata's cultural heritage with an unforgettable wildlife experience! Begin your adventure at Monkey Home, where you'll interact with friendly squirrel monkeys in their natural habitat. These playful creatures will climb onto your shoulders, creating perfect photo moments. Then, explore the historic heart of Puerto Plata on a guided city tour. Walk through the famous Umbrella Street, visit the iconic Pink Street (Paseo de Doña Blanca), explore the historic Fort San Felipe built in 1577, and discover the beautiful Central Park and Cathedral. Learn about Dominican rum, cigars, chocolate, and amber at local shops. This 4-hour tour offers the perfect balance of nature and culture. ✨ Ideal for families and culture enthusiasts ✨ Two unique experiences in one tour ✨ Learn about Dominican history and wildlife ✨ Great photo opportunities throughout ✨ Comfortable pace suitable for all ages"
          : "¡Combina lo mejor del patrimonio cultural de Puerto Plata con una experiencia inolvidable con la vida silvestre! Comienza tu aventura en Monkey Home, donde interactuarás con simpáticos monos ardilla en su hábitat natural. Estas juguetonas criaturas se subirán a tus hombros, creando momentos perfectos para fotos. Luego, explora el corazón histórico de Puerto Plata en un tour guiado por la ciudad. Camina por la famosa Calle de los Paraguas, visita la icónica Calle Rosa (Paseo de Doña Blanca), explora el histórico Fuerte San Felipe construido en 1577, y descubre el hermoso Parque Central y la Catedral. Aprende sobre el ron dominicano, cigarros, chocolate y ámbar en tiendas locales. Este tour de 4 horas ofrece el equilibrio perfecto entre naturaleza y cultura. ✨ Ideal para familias y entusiastas de la cultura ✨ Dos experiencias únicas en un tour ✨ Aprende sobre la historia y vida silvestre dominicana ✨ Excelentes oportunidades de fotos ✨ Ritmo cómodo para todas las edades",
      inclusions:
        language === 'en'
          ? [
              'Round-trip Transportation',
              'Bilingual Guide',
              'Monkey Sanctuary Entrance',
              'City Tour with All Stops',
              'Fort San Felipe Entrance',
              'Local Shop Visits',
              'Photo Opportunities',
            ]
          : [
              'Transporte de Ida y Vuelta',
              'Guía Bilingüe',
              'Entrada al Santuario de Monos',
              'Tour de Ciudad con Todas las Paradas',
              'Entrada al Fuerte San Felipe',
              'Visitas a Tiendas Locales',
              'Oportunidades para Fotos',
            ],
      highlights:
        language === 'en'
          ? [
              'Meet and interact with squirrel monkeys',
              'Explore colorful Umbrella Street',
              'Visit historic Fort San Felipe (1577)',
              'Discover local rum, cigars, and amber',
              'Perfect blend of nature and culture',
            ]
          : [
              'Conoce e interactúa con monos ardilla',
              'Explora la colorida Calle de los Paraguas',
              'Visita el histórico Fuerte San Felipe (1577)',
              'Descubre ron local, cigarros y ámbar',
              'Mezcla perfecta de naturaleza y cultura',
            ],
    },
    {
      id: 6,
      name:
        language === 'en'
          ? 'ATV & Buggies Adventure – Off-Road Thrills in Puerto Plata'
          : 'Aventura en ATV y Buggies – Emociones Todoterreno en Puerto Plata',
      category: language === 'en' ? 'adventure' : 'aventura',
      duration: language === 'en' ? '3-4 hours' : '3-4 horas',
      groupSize: language === 'en' ? '2-10 people' : '2-10 personas',
      price: 100,
      atvPrice: 95,
      buggyPrice: 100,
      rating: 4.9,
      reviews: 187,
      image:
        'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/b80633e8a21cf40a31a8b4cc497332aa.png',
      shortDescription:
        language === 'en'
          ? 'Experience the ultimate off-road adventure through stunning Dominican landscapes on powerful ATVs or exciting buggies.'
          : 'Experimenta la aventura todoterreno definitiva a través de impresionantes paisajes dominicanos en potentes ATVs o emocionantes buggies.',
      fullDescription:
        language === 'en'
          ? "Get ready for an adrenaline-pumping adventure through the beautiful countryside of Puerto Plata! Our ATV & Buggies Adventure tour takes you off the beaten path and into the heart of Dominican nature. Navigate through rugged trails, splash through rivers, climb hills with breathtaking views, and discover hidden gems that most tourists never see. Whether you choose the powerful ATV for a more personal experience or the exciting buggy for a shared adventure, you'll enjoy an unforgettable journey through tropical forests, local villages, and stunning mountain landscapes. Our experienced guides ensure your safety while showing you the best routes and most scenic spots. This tour is perfect for thrill-seekers and nature lovers alike. ✨ IMPORTANT: A valid driver's license is required to participate in this activity ✨ No previous off-road experience necessary ✨ All safety equipment provided ✨ Choose between ATV ($95) or Buggy ($100) ✨ Perfect for couples, friends, and adventure enthusiasts"
          : "¡Prepárate para una aventura llena de adrenalina a través del hermoso campo de Puerto Plata! Nuestro tour de Aventura en ATV y Buggies te lleva fuera de los caminos trillados y al corazón de la naturaleza dominicana. Navega por senderos accidentados, atraviesa ríos, sube colinas con vistas impresionantes y descubre joyas ocultas que la mayoría de los turistas nunca ven. Ya sea que elijas el potente ATV para una experiencia más personal o el emocionante buggy para una aventura compartida, disfrutarás de un viaje inolvidable a través de bosques tropicales, pueblos locales y paisajes montañosos impresionantes. Nuestros guías experimentados garantizan tu seguridad mientras te muestran las mejores rutas y los lugares más pintorescos. Este tour es perfecto tanto para buscadores de emociones como para amantes de la naturaleza. ✨ IMPORTANTE: Se requiere licencia de conducir válida para participar en esta actividad ✨ No se necesita experiencia previa en todoterreno ✨ Todo el equipo de seguridad incluido ✨ Elige entre ATV ($95) o Buggy ($100) ✨ Perfecto para parejas, amigos y entusiastas de la aventura",
      inclusions:
        language === 'en'
          ? [
              'Round-trip Transportation',
              'Professional Bilingual Guide',
              'ATV or Buggy Rental',
              'Safety Equipment (Helmet, Goggles)',
              'Safety Briefing & Training',
              'Photo Stops at Scenic Locations',
              'Insurance Coverage',
            ]
          : [
              'Transporte de Ida y Vuelta',
              'Guía Profesional Bilingüe',
              'Alquiler de ATV o Buggy',
              'Equipo de Seguridad (Casco, Gafas)',
              'Instrucción de Seguridad y Entrenamiento',
              'Paradas para Fotos en Lugares Escénicos',
              'Cobertura de Seguro',
            ],
      highlights:
        language === 'en'
          ? [
              'Drive through stunning mountain trails',
              'Cross rivers and splash through mud',
              'Visit authentic Dominican villages',
              'Enjoy panoramic views of Puerto Plata',
              'Professional safety equipment included',
              'Valid driver\'s license required',
            ]
          : [
              'Conduce por impresionantes senderos de montaña',
              'Cruza ríos y atraviesa el barro',
              'Visita auténticos pueblos dominicanos',
              'Disfruta de vistas panorámicas de Puerto Plata',
              'Equipo de seguridad profesional incluido',
              'Se requiere licencia de conducir válida',
            ],
    },
  ];

  const calculateTotal = () => {
    if (!selectedTour) return 0;
    const tour = tours.find((t) => t.id === selectedTour);
    if (!tour) return 0;

    let basePrice = tour.price;
    
    // Si es el tour de ATV/Buggy, usar el precio según la opción seleccionada
    if (tour.id === 6 && vehicleOption) {
      basePrice = vehicleOption === 'atv' ? (tour.atvPrice || 95) : (tour.buggyPrice || 100);
    }

    const adultsPrice = adults * basePrice;
    const kidsPrice = kids * (tour.kidsPrice || 50);
    const youngGuysPrice = youngGuys * (tour.youngGuysPrice || 55);

    return adultsPrice + kidsPrice + youngGuysPrice;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const tour = tours.find((t) => t.id === selectedTour);
      const formData = new URLSearchParams();
      formData.append('tour', tour?.name || '');
      if (tour?.id === 6 && vehicleOption) {
        formData.append('vehicleOption', vehicleOption === 'atv' ? 'ATV ($95)' : 'Buggy ($100)');
      }
      formData.append('adults', adults.toString());
      formData.append('kids', kids.toString());
      formData.append('youngGuys', youngGuys.toString());
      formData.append('date', date);
      formData.append('time', time);
      formData.append('fullName', fullName);
      formData.append('email', email);
      formData.append('phone', phone);
      formData.append('total', `$${calculateTotal()}`);

      const response = await fetch(
        'https://readdy.ai/api/public/form/submit/b7cf4d15025d84086817049d392fc8bc/tour-payment',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        }
      );

      if (response.ok) {
        // Redirect to PayPal payment page
        window.location.href = 'https://www.paypal.com/ncp/payment/JUWXYKXZ84ACG';
      } else {
        setSubmitMessage(
          language === 'en'
            ? 'Something went wrong. Please try again.'
            : 'Algo salió mal. Por favor intenta de nuevo.'
        );
      }
    } catch (error) {
      setSubmitMessage(
        language === 'en'
          ? 'Something went wrong. Please try again.'
          : 'Algo salió mal. Por favor intenta de nuevo.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img
                src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg"
                alt="Leury Tours"
                className="h-10 sm:h-12 w-auto"
              />
              <span className="text-lg sm:text-xl font-bold text-[#0A2463]">Leury Tours</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <Link to="/" className="text-sm font-medium text-[#0A2463] hover:text-[#00B4D8] transition-colors">
                {t.nav.home}
              </Link>
              <Link to="/about" className="text-sm font-medium text-[#0A2463] hover:text-[#00B4D8] transition-colors">
                {t.nav.about}
              </Link>
              <Link to="/tours" className="text-sm font-medium text-[#00B4D8] transition-colors">
                {t.nav.tours}
              </Link>
              <Link to="/transfers" className="text-sm font-medium text-[#0A2463] hover:text-[#00B4D8] transition-colors">
                {t.nav.transfers}
              </Link>
              <a href="/#reviews" className="text-sm font-medium text-[#0A2463] hover:text-[#00B4D8] transition-colors">
                {t.nav.reviews}
              </a>
              <a href="/#contact" className="text-sm font-medium text-[#0A2463] hover:text-[#00B4D8] transition-colors">
                {t.nav.contact}
              </a>
              <LanguageSelector scrolled={true} />
              <a
                href="/#booking"
                className="bg-[#FF6B6B] text-white px-5 xl:px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#ff5252] transition-all whitespace-nowrap"
              >
                {t.nav.bookNow}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-3 lg:hidden">
              <LanguageSelector scrolled={true} />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-[#0A2463]"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[60px] bg-white z-40 overflow-y-auto">
            <div className="px-4 py-6 space-y-4">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.home}
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.about}
              </Link>
              <Link
                to="/tours"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#00B4D8] py-3 border-b border-gray-100"
              >
                {t.nav.tours}
              </Link>
              <Link
                to="/transfers"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.transfers}
              </Link>
              <a
                href="/#reviews"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.reviews}
              </a>
              <a
                href="/#contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.contact}
              </a>
              <a
                href="/#booking"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full bg-[#FF6B6B] text-white px-6 py-3 rounded-full text-base font-semibold text-center mt-6"
              >
                {t.nav.bookNow}
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Banner */}
      <section className="relative h-64 sm:h-80 lg:h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stunning%20tropical%20waterfall%20cascading%20into%20turquoise%20pool%20in%20Dominican%20Republic%20rainforest%2C%20lush%20green%20vegetation%2C%20adventure%20tourism%20destination%2C%20professional%20travel%20photography%20vibrant%20nature%20colors%20bright%20sunny%20day%20simple%20natural%20background&width=1920&height=800&seq=tourshero&orientation=landscape"
            alt="Tours"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2463]/80 via-[#0A2463]/50 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <nav className="text-white/80 text-xs sm:text-sm mb-2 sm:mb-4">
            <Link to="/" className="hover:text-white">
              {t.nav.home}
            </Link>
            <span className="mx-2">&gt;</span>
            <span className="text-white">{t.nav.tours}</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-black text-white mb-2 sm:mb-4">
            {t.tours.heroTitle}
          </h1>
          <p className="text-sm sm:text-base lg:text-xl text-white/90 max-w-2xl">
            {t.tours.heroDescription}
          </p>
        </div>
      </section>

      {/* Tours Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-white to-[#FFF9F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-[#FF6B6B] text-xs sm:text-sm font-bold tracking-wider uppercase">
              {t.tours.ourExperiences}
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2463] mt-2 sm:mt-3 mb-3 sm:mb-4">
              {t.tours.chooseAdventure}
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm lg:text-base max-w-2xl mx-auto">
              {t.tours.chooseAdventureDesc}
            </p>
          </div>

          {/* Tours - Detailed Cards */}
          <div className="space-y-8 sm:space-y-12">
            {tours.map((tour, index) => (
              <div
                key={tour.id}
                id={tour.id === 2 ? 'city-tour' : undefined}
                className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
              >
                <div className={`grid lg:grid-cols-2 ${index % 2 === 1 ? 'lg:direction-rtl' : ''}`}>
                  {/* Image Side */}
                  <div
                    className={`relative h-56 sm:h-72 lg:h-auto lg:min-h-[400px] overflow-hidden ${
                      index % 2 === 1 ? 'lg:order-2' : ''
                    }`}
                  >
                    <img src={tour.image} alt={tour.name} className="w-full h-full object-cover object-center" />
                    <div className="absolute top-4 sm:top-6 left-4 sm:left-6">
                      <span className="bg-[#FF6B6B] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-xs font-bold uppercase">
                        {tour.category}
                      </span>
                    </div>
                    <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 flex items-center gap-2 sm:gap-3">
                      <div className="bg-white/95 backdrop-blur-sm rounded-full px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2">
                        <i className="ri-time-line text-[#00B4D8] text-xs sm:text-sm"></i>
                        <span className="text-xs sm:text-sm font-semibold text-[#0A2463]">{tour.duration}</span>
                      </div>
                      <div className="bg-white/95 backdrop-blur-sm rounded-full px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2">
                        <i className="ri-group-line text-[#00B4D8] text-xs sm:text-sm"></i>
                        <span className="text-xs sm:text-sm font-semibold text-[#0A2463]">{tour.groupSize}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content Side */}
                  <div
                    className={`p-5 sm:p-6 lg:p-8 xl:p-10 flex flex-col justify-center ${
                      index % 2 === 1 ? 'lg:order-1' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                      <div className="flex items-center gap-0.5 sm:gap-1">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`${
                              i < Math.floor(tour.rating) ? 'ri-star-fill' : 'ri-star-half-line'
                            } text-[#FFD700] text-xs sm:text-sm`}
                          ></i>
                        ))}
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-[#0A2463]">{tour.rating}</span>
                      <span className="text-xs sm:text-sm text-gray-500">
                        ({tour.reviews} {t.tours.reviews})
                      </span>
                    </div>

                    <h3 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-[#0A2463] mb-3 sm:mb-4">
                      {tour.name}
                    </h3>

                    <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                      {tour.shortDescription}
                    </p>

                    {/* Expandable Description */}
                    <div
                      className={`overflow-hidden transition-all duration-500 ${
                        expandedTour === tour.id ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
                        {tour.fullDescription}
                      </p>
                    </div>

                    <button
                      onClick={() => setExpandedTour(expandedTour === tour.id ? null : tour.id)}
                      className="text-[#00B4D8] text-xs sm:text-sm font-semibold flex items-center gap-1 mb-4 sm:mb-5 cursor-pointer hover:underline"
                    >
                      {expandedTour === tour.id ? t.tours.readLess : t.tours.readFullDescription}
                      <i
                        className={`ri-arrow-${expandedTour === tour.id ? 'up' : 'down'}-s-line text-sm sm:text-base`}
                      ></i>
                    </button>

                    {/* Highlights */}
                    <div className="mb-4 sm:mb-6">
                      <p className="text-[10px] sm:text-xs font-bold text-[#0A2463] uppercase tracking-wider mb-2 sm:mb-3">
                        {t.tours.tourHighlights}
                      </p>
                      <div className="space-y-1.5 sm:space-y-2">
                        {tour.highlights.map((highlight, idx) => (
                          <div key={idx} className="flex items-start gap-1.5 sm:gap-2">
                            <i className="ri-check-line text-[#00B4D8] text-xs sm:text-sm mt-0.5 flex-shrink-0"></i>
                            <span className="text-xs sm:text-sm text-gray-700">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Inclusions */}
                    <div className="mb-4 sm:mb-6">
                      <p className="text-[10px] sm:text-xs font-bold text-[#0A2463] uppercase tracking-wider mb-2 sm:mb-3">
                        {t.tours.whatsIncluded}
                      </p>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {tour.inclusions.map((item, idx) => (
                          <span
                            key={idx}
                            className="text-[10px] sm:text-xs bg-[#E8F7FA] text-[#00B4D8] px-2 sm:px-3 py-1 sm:py-1.5 rounded-full font-medium"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Price & CTA */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between pt-4 sm:pt-5 border-t border-gray-100 gap-3 sm:gap-0">
                      <div>
                        <p className="text-[10px] sm:text-xs text-gray-500">{t.tours.startingFrom}</p>
                        <div className="flex items-baseline gap-1 flex-wrap">
                          <span className="text-2xl sm:text-3xl font-bold text-[#00B4D8]">
                            ${tour.id === 6 ? '95 - 100' : tour.price}
                          </span>
                          <span className="text-[10px] sm:text-sm text-gray-500">
                            {tour.id === 6 
                              ? `/ ${t.tours.adults}` 
                              : `/ ${t.tours.adults} / ${t.tours.kids}: $50 / ${t.tours.youngGuys}: $55`
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#FF6B6B] text-xs sm:text-sm font-bold tracking-wider uppercase">
              {language === 'en' ? 'BOOK YOUR TOUR' : 'RESERVA TU TOUR'}
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2463] mt-2 sm:mt-3 mb-3 sm:mb-4">
              {language === 'en' ? 'Complete Your Booking' : 'Completa tu Reserva'}
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm lg:text-base max-w-2xl mx-auto">
              {language === 'en'
                ? 'Select your tour, choose the number of participants, and proceed with payment'
                : 'Selecciona tu tour, elige el número de participantes y procede con el pago'}
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-gradient-to-br from-[#F8FBFF] to-white rounded-2xl sm:rounded-3xl shadow-xl p-5 sm:p-6 lg:p-8 xl:p-10"
            data-readdy-form
          >
            {/* Tour Selection */}
            <div className="mb-5 sm:mb-6">
              <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                {language === 'en' ? 'Select Tour' : 'Seleccionar Tour'}
              </label>
              <select
                name="tour"
                value={selectedTour || ''}
                onChange={(e) => {
                  setSelectedTour(Number(e.target.value));
                  setVehicleOption(null);
                }}
                required
                className="w-full px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border-2 border-gray-200 focus:border-[#00B4D8] focus:outline-none text-xs sm:text-sm transition-colors"
              >
                <option value="">
                  {language === 'en' ? 'Choose a tour...' : 'Elige un tour...'}
                </option>
                {tours.map((tour) => (
                  <option key={tour.id} value={tour.id}>
                    {tour.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Vehicle Option for ATV & Buggies Tour */}
            {selectedTour === 6 && (
              <div className="mb-5 sm:mb-6">
                <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                  {language === 'en' ? 'Select Vehicle Option' : 'Seleccionar Tipo de Vehículo'}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <button
                    type="button"
                    onClick={() => setVehicleOption('atv')}
                    className={`p-4 sm:p-5 rounded-xl border-2 transition-all cursor-pointer ${
                      vehicleOption === 'atv'
                        ? 'border-[#00B4D8] bg-[#00B4D8]/10'
                        : 'border-gray-200 hover:border-[#00B4D8]/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        vehicleOption === 'atv' ? 'border-[#00B4D8]' : 'border-gray-300'
                      }`}>
                        {vehicleOption === 'atv' && (
                          <div className="w-3 h-3 rounded-full bg-[#00B4D8]"></div>
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-[#0A2463] text-sm sm:text-base">ATV Experience</p>
                        <p className="text-[#00B4D8] font-bold text-lg sm:text-xl">$95 <span className="text-xs sm:text-sm text-gray-500 font-normal">/ {language === 'en' ? 'per adult' : 'por adulto'}</span></p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setVehicleOption('buggy')}
                    className={`p-4 sm:p-5 rounded-xl border-2 transition-all cursor-pointer ${
                      vehicleOption === 'buggy'
                        ? 'border-[#00B4D8] bg-[#00B4D8]/10'
                        : 'border-gray-200 hover:border-[#00B4D8]/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        vehicleOption === 'buggy' ? 'border-[#00B4D8]' : 'border-gray-300'
                      }`}>
                        {vehicleOption === 'buggy' && (
                          <div className="w-3 h-3 rounded-full bg-[#00B4D8]"></div>
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-[#0A2463] text-sm sm:text-base">Buggy Experience</p>
                        <p className="text-[#00B4D8] font-bold text-lg sm:text-xl">$100 <span className="text-xs sm:text-sm text-gray-500 font-normal">/ {language === 'en' ? 'per adult' : 'por adulto'}</span></p>
                      </div>
                    </div>
                  </button>
                </div>
                <p className="mt-2 sm:mt-3 text-[10px] sm:text-xs text-amber-600 flex items-center gap-1">
                  <i className="ri-alert-line"></i>
                  {language === 'en' 
                    ? 'Important: A valid driver\'s license is required to participate in this activity'
                    : 'Importante: Se requiere licencia de conducir válida para participar en esta actividad'}
                </p>
              </div>
            )}

            {/* Participants */}
            <div className={`grid grid-cols-1 ${selectedTour === 6 ? 'sm:grid-cols-1 max-w-xs' : 'sm:grid-cols-3'} gap-4 sm:gap-6 mb-5 sm:mb-6`}>
              <div>
                <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                  {language === 'en' ? 'Adults' : 'Adultos'} (${selectedTour === 6 && vehicleOption ? (vehicleOption === 'atv' ? 95 : 100) : (selectedTour ? tours.find((t) => t.id === selectedTour)?.price : 0)})
                </label>
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setAdults(Math.max(1, adults - 1))}
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#00B4D8] text-white font-bold hover:bg-[#0096B8] transition-colors flex items-center justify-center"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    name="adults"
                    value={adults}
                    readOnly
                    className="w-full px-2 sm:px-4 py-2.5 sm:py-3 rounded-xl border-2 border-gray-200 text-center text-xs sm:text-sm font-semibold"
                  />
                  <button
                    type="button"
                    onClick={() => setAdults(adults + 1)}
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#00B4D8] text-white font-bold hover:bg-[#0096B8] transition-colors flex items-center justify-center"
                  >
                    +
                  </button>
                </div>
              </div>

              {selectedTour !== 6 && (
                <>
                  <div>
                    <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                      {language === 'en' ? 'Kids (0-12)' : 'Niños (0-12)'} ($50)
                    </label>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <button
                        type="button"
                        onClick={() => setKids(Math.max(0, kids - 1))}
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#00B4D8] text-white font-bold hover:bg-[#0096B8] transition-colors flex items-center justify-center"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        name="kids"
                        value={kids}
                        readOnly
                        className="w-full px-2 sm:px-4 py-2.5 sm:py-3 rounded-xl border-2 border-gray-200 text-center text-xs sm:text-sm font-semibold"
                      />
                      <button
                        type="button"
                        onClick={() => setKids(kids + 1)}
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#00B4D8] text-white font-bold hover:bg-[#0096B8] transition-colors flex items-center justify-center"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                      {language === 'en' ? 'Teens (13-17)' : 'Jóvenes (13-17)'} ($55)
                    </label>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <button
                        type="button"
                        onClick={() => setYoungGuys(Math.max(0, youngGuys - 1))}
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#00B4D8] text-white font-bold hover:bg-[#0096B8] transition-colors flex items-center justify-center"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        name="youngGuys"
                        value={youngGuys}
                        readOnly
                        className="w-full px-2 sm:px-4 py-2.5 sm:py-3 rounded-xl border-2 border-gray-200 text-center text-xs sm:text-sm font-semibold"
                      />
                      <button
                        type="button"
                        onClick={() => setYoungGuys(youngGuys + 1)}
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#00B4D8] text-white font-bold hover:bg-[#0096B8] transition-colors flex items-center justify-center"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-5 sm:mb-6">
              <div>
                <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                  {language === 'en' ? 'Date' : 'Fecha'}
                </label>
                <input
                  type="date"
                  name="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  className="w-full px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border-2 border-gray-200 focus:border-[#00B4D8] focus:outline-none text-xs sm:text-sm transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                  {language === 'en' ? 'Time' : 'Hora'}
                </label>
                <input
                  type="time"
                  name="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  required
                  className="w-full px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border-2 border-gray-200 focus:border-[#00B4D8] focus:outline-none text-xs sm:text-sm transition-colors"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
              <div>
                <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                  {language === 'en' ? 'Full Name' : 'Nombre Completo'}
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  placeholder={language === 'en' ? 'Your full name' : 'Tu nombre completo'}
                  className="w-full px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border-2 border-gray-200 focus:border-[#00B4D8] focus:outline-none text-xs sm:text-sm transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                  {language === 'en' ? 'Email' : 'Correo Electrónico'}
                </label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={language === 'en' ? 'your@email.com' : 'tu@email.com'}
                  className="w-full px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border-2 border-gray-200 focus:border-[#00B4D8] focus:outline-none text-xs sm:text-sm transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-bold text-[#0A2463] mb-2 sm:mb-3">
                  {language === 'en' ? 'Phone' : 'Teléfono'}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  placeholder="+1 (809) 555-1234"
                  className="w-full px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border-2 border-gray-200 focus:border-[#00B4D8] focus:outline-none text-xs sm:text-sm transition-colors"
                />
              </div>
            </div>

            {/* Total & Submit */}
            <div className="bg-gradient-to-r from-[#0A2463] to-[#00B4D8] rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-5 sm:mb-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <span className="text-white/90 text-xs sm:text-sm font-medium">
                  {language === 'en' ? 'Total Amount' : 'Monto Total'}
                </span>
                <span className="text-2xl sm:text-3xl font-bold text-white">${calculateTotal()}</span>
              </div>
              <div className="text-white/70 text-[10px] sm:text-xs">
                {language === 'en'
                  ? `${adults} adult${adults > 1 ? 's' : ''} + ${kids} kid${kids !== 1 ? 's' : ''} + ${youngGuys} teen${youngGuys !== 1 ? 's' : ''}`
                  : `${adults} adulto${adults > 1 ? 's' : ''} + ${kids} niño${kids !== 1 ? 's' : ''} + ${youngGuys} joven${youngGuys !== 1 ? 'es' : ''}`}
              </div>
            </div>

            <a
              href="https://www.paypal.com/ncp/payment/JUWXYKXZ84ACG"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full bg-[#FF6B6B] text-white py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:bg-[#ff5252] transition-all flex items-center justify-center gap-2 whitespace-nowrap ${!selectedTour ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
            >
              <i className="ri-lock-line"></i>
              {language === 'en' ? 'Proceed to Payment' : 'Proceder al Pago'}
            </a>

            {submitMessage && (
              <div
                className={`mt-4 p-3 sm:p-4 rounded-xl text-xs sm:text-sm text-center ${
                  submitMessage.includes('éxito') || submitMessage.includes('success')
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {submitMessage}
              </div>
            )}

            <p className="text-center text-[10px] sm:text-xs text-gray-500 mt-3 sm:mt-4">
              <i className="ri-shield-check-line text-[#00B4D8]"></i>{' '}
              {language === 'en'
                ? 'Secure payment processing. Your information is protected.'
                : 'Procesamiento de pago seguro. Tu información está protegida.'}
            </p>
          </form>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2463] mb-3 sm:mb-4">
              {t.tours.whyTourWithUs}
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm lg:text-base">{t.tours.whyTourDesc}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#00B4D8]/10 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-shield-check-line text-[#00B4D8] text-xl sm:text-2xl"></i>
              </div>
              <h4 className="font-bold text-[#0A2463] text-xs sm:text-sm lg:text-base mb-1 sm:mb-2">
                {t.tours.safetyFirst}
              </h4>
              <p className="text-gray-600 text-[10px] sm:text-xs lg:text-sm">{t.tours.safetyDesc}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#FF6B6B]/10 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-user-star-line text-[#FF6B6B] text-xl sm:text-2xl"></i>
              </div>
              <h4 className="font-bold text-[#0A2463] text-xs sm:text-sm lg:text-base mb-1 sm:mb-2">
                {t.tours.expertGuides}
              </h4>
              <p className="text-gray-600 text-[10px] sm:text-xs lg:text-sm">{t.tours.expertDesc}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#48CAE4]/10 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-group-line text-[#48CAE4] text-xl sm:text-2xl"></i>
              </div>
              <h4 className="font-bold text-[#0A2463] text-xs sm:text-sm lg:text-base mb-1 sm:mb-2">
                {t.tours.smallGroups}
              </h4>
              <p className="text-gray-600 text-[10px] sm:text-xs lg:text-sm">{t.tours.smallGroupsDesc}</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#FFD700]/10 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-money-dollar-circle-line text-[#FFD700] text-xl sm:text-2xl"></i>
              </div>
              <h4 className="font-bold text-[#0A2463] text-xs sm:text-sm lg:text-base mb-1 sm:mb-2">
                {t.tours.bestValue}
              </h4>
              <p className="text-gray-600 text-[10px] sm:text-xs lg:text-sm">{t.tours.bestValueDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#0A2463] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0c16.569 0 30 13.431 30 30 0 16.569-13.431 30-30 30C13.431 60 0 46.569 0 30 0 13.431 13.431 0 30 0z\' fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
            }}
          ></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
            {t.tours.cantFind}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8">
            {t.tours.cantFindDesc}
          </p>
          <a
            href="/#contact"
            className="inline-block bg-[#FF6B6B] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-bold text-sm sm:text-base hover:bg-[#ff5252] transition-all whitespace-nowrap"
          >
            {t.tours.requestCustomTour}
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2463] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 lg:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-16">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <img
                  src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg"
                  alt="Leury Tours"
                  className="h-8 sm:h-10 w-auto"
                />
                <span className="text-lg sm:text-xl font-bold">Leury Tours</span>
              </div>
              <p className="text-white/70 text-xs sm:text-sm mb-4 sm:mb-6">
                {t.home?.heroSubtitle || "Puerto Plata's Premier Tour Experience"}
              </p>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-phone-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm">+1 (809)-461-9001</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-mail-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm break-all">leury2124@gmail.com</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">
                {t.footer.explore}
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <Link
                    to="/transfers"
                    className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors"
                  >
                    {t.footer.transfers}
                  </Link>
                </li>
                <li>
                  <Link to="/tours" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">
                    {t.footer.tours}
                  </Link>
                </li>
                <li>
                  <a href="/#contact" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">
                    {t.footer.customServices}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">
                {t.footer.followUs}
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="https://www.instagram.com/leury2124?igsh=bHdpNHcxbmVtOQ%3D%3D&utm_source=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors"
                  >
                    Instagram
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.facebook.com/share/1VPJPAepMd/?mibextid=wwXIfr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors"
                  >
                    Facebook
                  </a>
                </li>
                <li>
                  <a
                    href="https://tripadvisor.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors"
                  >
                    TripAdvisor
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <Link
                to="/booking"
                className="block w-full bg-[#FF6B6B] text-white py-2.5 sm:py-3 rounded-full font-semibold text-xs sm:text-sm hover:bg-[#ff5252] transition-all text-center mb-3 sm:mb-4 whitespace-nowrap"
              >
                {t.nav.bookNow}
              </Link>
              <Link
                to="/booking"
                className="block w-full border-2 border-white text-white py-2.5 sm:py-3 rounded-full font-semibold text-xs sm:text-sm hover:bg-white hover:text-[#0A2463] transition-all text-center whitespace-nowrap"
              >
                {t.nav.contact}
              </Link>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 sm:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              <p className="text-white/60 text-xs sm:text-sm text-center sm:text-left">
                {t.footer.allRightsReserved}
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <Link
                  to="/privacy"
                  className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors"
                >
                  {t.footer.privacyPolicy}
                </Link>
                <Link
                  to="/terms"
                  className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors"
                >
                  {t.footer.termsConditions}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
